import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { CheckCircle2, Loader2, Package, X } from "lucide-react";
import BusinessProposalHeader from "../components/business-proposal/BusinessProposalHeader";
import BusinessProposalFooter from "../components/business-proposal/BusinessProposalFooter";
import TermsModal from "../components/business-proposal/TermsModal";
import PrivacyModal from "../components/business-proposal/PrivacyModal";
import BusinessProposalPaymentForm from "../components/business-proposal/BusinessProposalPaymentForm";
import { getBusinessProposal, getBusinessProposalErrorMessage } from "../services/businessProposal";
import type { BusinessOnboardingCompleteResult } from "../services/businessProposal";
import {
  BusinessProposalData,
  formatProposalAmount,
  getDemoBusinessProposal,
  getPaymentTypeLabel,
  isDemoOnboardingId,
} from "../lib/businessProposalParams";

import { getGlossaPayPublishableKey } from "../lib/stripePublishableKey";

type ProposalStep = "details" | "payment" | "success";

const BusinessProposalPage: React.FC = () => {
  const glossaPayPublishableKey = React.useMemo(() => getGlossaPayPublishableKey(), []);
  const stripePromise = React.useMemo(
    () => (glossaPayPublishableKey ? loadStripe(glossaPayPublishableKey) : null),
    [glossaPayPublishableKey],
  );
  const { encodedSubscriptionId = "" } = useParams<{ encodedSubscriptionId: string }>();
  const [proposal, setProposal] = useState<BusinessProposalData | null>(null);
  const [isDemo, setIsDemo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<ProposalStep>("details");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [paymentResult, setPaymentResult] = useState<BusinessOnboardingCompleteResult | null>(
    null,
  );

  useEffect(() => {
    if (!encodedSubscriptionId) {
      setError("Onboarding link is missing.");
      setLoading(false);
      return;
    }

    const loadProposal = async () => {
      try {
        setLoading(true);
        setError(null);

        if (isDemoOnboardingId(encodedSubscriptionId)) {
          setProposal(getDemoBusinessProposal(encodedSubscriptionId));
          setIsDemo(true);
          return;
        }

        const data = await getBusinessProposal(encodedSubscriptionId);
        setIsDemo(false);
        if (data.status === "paid") {
          setStep("success");
        } else if (data.status === "expired") {
          setError("This onboarding link is no longer available. Please contact Glossa support.");
          return;
        }
        setProposal(data);
      } catch (err) {
        setProposal(null);
        setError(getBusinessProposalErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    loadProposal();
  }, [encodedSubscriptionId]);

  const handleProceed = () => {
    if (!acceptedTerms) return;
    setStep("payment");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <BusinessProposalHeader />
        <div className="flex-1 flex flex-col items-center justify-center text-gray-500 px-4">
          <Loader2 className="w-8 h-8 animate-spin mb-3 text-[#885ABB]" />
          <p>Loading your business onboarding...</p>
        </div>
        <BusinessProposalFooter />
      </div>
    );
  }

  if (error || !proposal) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <BusinessProposalHeader />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-xl border border-red-100 max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Unable to load onboarding</h2>
            <p className="text-gray-600">{error || "This onboarding link could not be found."}</p>
          </div>
        </div>
        <BusinessProposalFooter />
      </div>
    );
  }

  const currencySymbol = proposal.currencySymbol || "£";

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <BusinessProposalHeader />

      <main className="flex-1 px-4 sm:px-6 py-8 sm:py-10">
        {isDemo && (
          <div className="max-w-3xl mx-auto mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <span className="font-semibold">Demo preview</span> — use `/business-onboarding/demo`
            for UI preview only. Live links load real subscription data from the API.
          </div>
        )}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 border-t-[6px] border-t-[#885ABB] overflow-hidden">
            <div className="p-6 sm:p-8 md:p-10">
              {step === "success" ? (
                <div className="text-center py-6 sm:py-10">
                  <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-green-500" />
                  </div>
                  <h1 className="text-3xl font-bold text-[#402B69] mb-3">Payment successful</h1>
                  <p className="text-gray-600 leading-relaxed max-w-lg mx-auto mb-2">
                    Thank you. Your Glossa subscription for{" "}
                    <span className="font-semibold text-gray-800">{proposal.packageName}</span>{" "}
                    is now active.
                  </p>
                  {paymentResult?.next_charge_at && (
                    <p className="text-sm text-gray-500 mb-2">
                      Your next payment is scheduled for{" "}
                      <span className="font-medium text-gray-700">
                        {new Date(paymentResult.next_charge_at).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </span>
                      .
                    </p>
                  )}
                  <p className="text-sm text-gray-500">
                    You can sign in to Glossa using your account email. Contact{" "}
                    <a
                      href="mailto:support@getglossa.com"
                      className="font-medium text-[#885ABB] hover:text-[#7647a8]"
                    >
                      support@getglossa.com
                    </a>{" "}
                    if you need help getting started.
                  </p>
                </div>
              ) : step === "payment" ? (
                stripePromise ? (
                  <Elements stripe={stripePromise}>
                    <BusinessProposalPaymentForm
                      proposal={proposal}
                      isDemo={isDemo}
                      onSuccess={(result) => {
                        setPaymentResult(result ?? null);
                        setStep("success");
                      }}
                      onBack={() => setStep("details")}
                    />
                  </Elements>
                ) : (
                  <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-4 text-sm text-amber-800">
                    Stripe is not configured for Glossa Pay. Add{" "}
                    <code className="text-xs">REACT_APP_GLOSSA_PAY_STRIPE_PUBLISHABLE_KEY</code>{" "}
                    to your <code className="text-xs">.env</code> (must match backend{" "}
                    <code className="text-xs">GLOSSA_PAY_STRIPE_KEY</code>).
                  </div>
                )
              ) : (
                <div className="space-y-8">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-widest text-[#885ABB] mb-2">
                      Glossa Subscription Onboarding
                    </p>
                    <h1 className="text-3xl sm:text-4xl font-bold text-[#402B69] tracking-tight">
                      Thanks for your interest
                    </h1>
                    <p className="mt-4 text-gray-600 leading-relaxed">
                      Thank you for your interest in Glossa. Review your subscription package
                      details below and proceed when you are ready to get started.
                    </p>
                    {proposal.businessName && (
                      <p className="mt-3 text-sm text-gray-500">
                        Prepared for{" "}
                        <span className="font-semibold text-gray-700">{proposal.businessName}</span>
                      </p>
                    )}
                  </div>

                  <div className="rounded-2xl border border-[#E5E0EB] bg-[#FAF8FD] p-5 sm:p-6 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-xl bg-[#885ABB]/10 text-[#885ABB]">
                        <Package className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-widest text-gray-400 font-bold">
                          Package details
                        </p>
                        <h2 className="text-xl font-bold text-[#402B69]">{proposal.packageName}</h2>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4 pt-2">
                      <div className="rounded-xl bg-white border border-gray-100 p-4">
                        <p className="text-xs uppercase tracking-widest text-gray-400 font-bold mb-1">
                          Amount
                        </p>
                        <p className="text-2xl font-bold text-[#885ABB]">
                          {formatProposalAmount(
                            proposal.amount,
                            currencySymbol,
                            proposal.paymentType,
                          )}
                        </p>
                      </div>
                      <div className="rounded-xl bg-white border border-gray-100 p-4">
                        <p className="text-xs uppercase tracking-widest text-gray-400 font-bold mb-1">
                          Payment type
                        </p>
                        <p className="text-2xl font-bold text-[#402B69]">
                          {getPaymentTypeLabel(proposal.paymentType)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-[#885ABB] focus:ring-[#885ABB]"
                      />
                      <span className="text-sm text-gray-600 leading-relaxed">
                        I have read and agree to the{" "}
                        <button
                          type="button"
                          onClick={() => setShowTerms(true)}
                          className="font-semibold text-[#885ABB] hover:text-[#7647a8] underline underline-offset-2"
                        >
                          Terms & Conditions
                        </button>{" "}
                        and{" "}
                        <button
                          type="button"
                          onClick={() => setShowPrivacy(true)}
                          className="font-semibold text-[#885ABB] hover:text-[#7647a8] underline underline-offset-2"
                        >
                          Privacy Policy
                        </button>
                        .
                      </span>
                    </label>
                  </div>

                  <button
                    type="button"
                    onClick={handleProceed}
                    disabled={!acceptedTerms}
                    className="w-full h-12 sm:h-14 bg-[#885ABB] hover:bg-[#7647a8] text-white rounded-xl font-bold text-sm sm:text-base transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#885ABB]/20"
                  >
                    Proceed to payment
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <BusinessProposalFooter email="support@getglossa.com" />

      <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
      <PrivacyModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />
    </div>
  );
};

export default BusinessProposalPage;
